import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

// Pages
import Landing from "@/pages/Landing";
import Terms from "@/pages/Terms";
import Favorites from "@/pages/Favorites";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Onboarding from "@/pages/Onboarding";
import Discover from "@/pages/Discover";
import Profile from "@/pages/Profile";
import Credits from "@/pages/Credits";
import PremiumOffers from "@/pages/PremiumOffers";
import Leaderboard from "@/pages/Leaderboard";
import Events from "@/pages/Events";
import Chat from "@/pages/Chat";
import Conversation from "@/pages/Conversation";
import Meetups from "@/pages/Meetups";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Analytics from "@/pages/Analytics";
import Earnings from "@/pages/Earnings";
import InviteTeaser from "@/pages/InviteTeaser";
import HelpChat from "@/components/HelpChat";
import { connectRealtime, disconnectRealtime } from "@/lib/realtime";
import { AppModeProvider } from "@/context/AppModeContext";
import { LocaleProvider, useLocale } from "@/i18n/LocaleContext";
import TestDriveTour from "@/components/TestDriveTour";
import SafetyCheckinBanner from "@/components/SafetyCheckinBanner";
import { fireHeartsConfetti, fireDiamondsConfetti } from "@/lib/confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance with auth
export const api = axios.create({
  baseURL: API,
  withCredentials: true
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs onboarding (Google OAuth without gender)
  if (user.needs_onboarding && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Auth Callback Component for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/login");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await api.post("/auth/google-session", { session_id: sessionId });
        const { user, session_token } = response.data;
        
        localStorage.setItem("token", session_token);
        (localStorage.getItem("app_mode") === "dluxe" ? fireDiamondsConfetti : fireHeartsConfetti)();
        
        // Redirect based on onboarding status
        if (user.needs_onboarding) {
          navigate("/onboarding", { state: { user } });
        } else {
          navigate("/discover", { state: { user } });
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate("/login");
      }
    };

    processSession();
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/invite/:code" element={<InviteTeaser />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
      <Route path="/premium-offers" element={<ProtectedRoute><PremiumOffers /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/chat/:conversationId" element={<ProtectedRoute><Conversation /></ProtectedRoute>} />
      <Route path="/meetups" element={<ProtectedRoute><Meetups /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
      <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
    </Routes>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      connectRealtime();
    } else {
      disconnectRealtime();
    }
  }, [user]);

  const login = useCallback(async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", response.data.access_token);
    setUser(response.data.user);
    return response.data;
  }, []);

  const register = useCallback(async (data) => {
    const response = await api.post("/auth/register", data);
    localStorage.setItem("token", response.data.access_token);
    setUser(response.data.user);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error (non-critical):", error);
      // Ignore logout errors - still clear local state
    }
    localStorage.removeItem("token");
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const authValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    checkAuth
  }), [user, loading, login, register, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

function LocaleUserSync() {
  const { user } = useAuth();
  const { lang, country, setLang, setCountry } = useLocale();
  useEffect(() => {
    if (!user) return;
    if (user.country && user.country !== country) setCountry(user.country);
    if (user.language && user.language !== lang) setLang(user.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.country, user?.language]);
  return null;
}

function InviteClaimer() {
  const { user } = useAuth();
  const claiming = useRef(false);
  useEffect(() => {
    const code = localStorage.getItem("pending_companion_invite");
    if (!user || user.needs_onboarding || !code || claiming.current) return;
    claiming.current = true;
    api.post("/companion-invites/claim", { code })
      .then((r) => {
        toast.success(`💌 ${r.data.companion_name} left you a surprise — check your chat!`, { duration: 6000 });
      })
      .catch(() => {})
      .finally(() => {
        localStorage.removeItem("pending_companion_invite");
        claiming.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.needs_onboarding]);
  useEffect(() => {
    const sa = localStorage.getItem("pending_sa_code");
    if (!user || user.needs_onboarding || !sa) return;
    api.post("/social-invites/claim", { code: sa })
      .then((r) => {
        localStorage.removeItem("pending_sa_code");
        if (r.data?.admirer_name) {
          toast.success(`💌 Your secret admirer is ${r.data.admirer_name} — the full story is in your chat!`, { duration: 9000 });
        }
      })
      .catch((e) => {
        if (e.response?.status === 404) localStorage.removeItem("pending_sa_code");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.needs_onboarding]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocaleProvider>
        <LocaleUserSync />
        <InviteClaimer />
        <AppModeProvider>
        <div className="min-h-screen bg-background">
          <AppRouter />
          <TestDriveTour />
          <SafetyCheckinBanner />
          <HelpChat />
          <Toaster position="top-center" richColors theme="dark" offset={72} />
        </div>
        </AppModeProvider>
        </LocaleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
